import { Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { AuthProvider } from './context/AuthContext'
import { ProgressProvider } from './context/ProgressContext'
import { ConfigProvider } from './context/ConfigContext'
import ProtectedRoute from './components/ProtectedRoute'
import AppShell from './components/AppShell'
import Home from './pages/Home'
import CoursePage from './pages/CoursePage'
import BuildModulesPage from './pages/BuildModulesPage'
import ModuleStudioPage from './pages/ModuleStudioPage'
import ModuleRouter from './pages/ModuleRouter'
import LoginPage from './pages/LoginPage'
import AdminPage from './pages/AdminPage'
import AdminHubPage from './pages/AdminHubPage'
import AdminDegreesPage from './pages/AdminDegreesPage'
import AdminPeoplePage from './pages/AdminPeoplePage'
import AdminDepartmentsPage from './pages/AdminDepartmentsPage'
import AdminModulesPage from './pages/AdminModulesPage'
import AdminLessonsPage from './pages/AdminLessonsPage'
import AdminCoursesPage from './pages/AdminCoursesPage'
import { legacyModulesTarget, moduleHref } from './data/courses'
import { useConfig } from './context/ConfigContext'
import { mergeCatalogue, courseForModule } from './data/catalogue'
import { modules as builtInModules } from './data/modules'

/**
 * `/modules` and `/modules?track=<id>`, kept alive for bookmarks.
 *
 * A plain <Navigate> would drop the query string, which is where the course id
 * lives — so the mapping is done here. See legacyModulesTarget.
 */
function ModulesRedirect() {
  const [searchParams] = useSearchParams()
  return <Navigate to={legacyModulesTarget(searchParams)} replace />
}

/**
 * `/module/<id>` — the address before a module could be in more than one course.
 *
 * It CANONICALISES rather than renders. Two routes rendering one page is two
 * things to keep in step, which is the argument that turned `/modules` into a
 * redirect; the same applies here. Old bookmarks, cross-course links written
 * into module copy, and the admin lists all still work, and all end up on the
 * course-scoped URL.
 *
 * A module in no course has nowhere to be scoped to, so it renders unscoped —
 * still openable, which beats sending someone home over a technicality.
 */
function LegacyModuleRedirect() {
  const { moduleId } = useParams()
  const { courses, customModules, loading } = useConfig()
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    )
  }
  const course = courseForModule(moduleId, courses, mergeCatalogue(builtInModules, customModules || []))
  if (!course) return <ModuleRouter />
  return <Navigate to={moduleHref(course.id, moduleId)} replace />
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function App() {
  return (
    <AuthProvider>
      <ConfigProvider>
      <ProgressProvider>
        <div className="min-h-screen bg-slate-50">
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AppShell>
                    <AdminHubPage />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            {[
              ['/admin/progress', AdminPage],
              ['/admin/degrees', AdminDegreesPage],
              ['/admin/people', AdminPeoplePage],
              ['/admin/departments', AdminDepartmentsPage],
              ['/admin/courses', AdminCoursesPage],
              ['/admin/modules', AdminModulesPage],
              ['/admin/lessons', AdminLessonsPage],
              ['/admin/docs', BuildModulesPage],
            ].map(([path, Page]) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute adminOnly>
                    <AppShell>
                      <Page />
                    </AppShell>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path="/admin/studio"
              element={
                <ProtectedRoute adminOnly>
                  <ModuleStudioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/studio/:draftId"
              element={
                <ProtectedRoute adminOnly>
                  <ModuleStudioPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Home />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/courses/:courseId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <CoursePage />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            {/* Backwards compatible redirects */}
            {/* /admin/guides rendered the same page as /admin/docs. Two routes
                for one page is two things to keep in step, so it redirects now
                rather than duplicating — anyone holding the old link still
                arrives. */}
            {/* The course index is the home page, so `/courses` is not a
                second page listing the same things — it points at the one that
                already does. `/modules` carried an optional `?track=`, which is
                mapped rather than dropped. */}
            <Route path="/courses" element={<Navigate to="/" replace />} />
            <Route path="/modules" element={<ModulesRedirect />} />
            <Route path="/admin/guides" element={<Navigate to="/admin/docs" replace />} />
            <Route path="/build-modules" element={<Navigate to="/admin/docs" replace />} />
            <Route path="/studio" element={<Navigate to="/admin/studio" replace />} />
            <Route path="/studio/:draftId" element={<Navigate to="/admin/studio" replace />} />
            {/* Canonical: the course is in the path, so a module in several
                courses is read in the one you came from. */}
            <Route
              path="/courses/:courseId/module/:moduleId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ModuleRouter />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/module/:moduleId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <LegacyModuleRedirect />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </ProgressProvider>
      </ConfigProvider>
    </AuthProvider>
  )
}

export default App
